import "@/App.css";
import { lazy, Suspense, useEffect, useState } from "react";
import { flushSync } from "react-dom";
import { BrowserRouter, Routes, Route, Navigate, useLocation, useNavigate, useParams } from "react-router-dom";
import { Toaster } from "@/components/ui/sonner";
import { toast } from "sonner";
import ErrorBoundary from "@/components/ErrorBoundary";
import GuestNav from "@/components/GuestNav";
import LandingPage from "@/pages/LandingPage";
import { auth } from "@/lib/firebase";
import { installDiagnostics } from "@/lib/diagnostics";

// Global error hooks + toast.error wrap, before any route renders, so
// failures during the very first load are captured too.
installDiagnostics({ getUser: () => auth.currentUser, toast });

// Lazy-loaded pages split into plan/live chunks. LandingPage stays eager so the
// marketing root has zero chunk round-trip on first paint.
const AdminPage     = lazy(() => import(/* webpackChunkName: "plan" */ "@/pages/AdminPage"));
const SeatingLesson = lazy(() => import(/* webpackChunkName: "plan" */ "@/pages/learn/SeatingLesson"));
const LiveLauncher  = lazy(() => import(/* webpackChunkName: "live" */ "@/pages/LiveLauncher"));
const CheckinPage   = lazy(() => import(/* webpackChunkName: "live" */ "@/pages/CheckinPage"));
const ReceptionPage = lazy(() => import(/* webpackChunkName: "live" */ "@/pages/ReceptionPage"));
const LuckyDrawPage = lazy(() => import(/* webpackChunkName: "live" */ "@/pages/LuckyDrawPage"));
const TVPage        = lazy(() => import(/* webpackChunkName: "live" */ "@/pages/TVPage"));
const LobbyPage     = lazy(() => import(/* webpackChunkName: "live" */ "@/pages/LobbyPage"));
const GalleryPage   = lazy(() => import(/* webpackChunkName: "live" */ "@/pages/GalleryPage"));
const QuizPage      = lazy(() => import(/* webpackChunkName: "live" */ "@/pages/QuizPage"));
const PhotosPage    = lazy(() => import(/* webpackChunkName: "live" */ "@/pages/PhotosPage"));
// InvitePage gets its own chunk: RSVP links are the highest-traffic guest entry
// point and must not pull in the heavy day-of live bundle (check-in, TV, quiz…).
const InvitePage    = lazy(() => import(/* webpackChunkName: "invite" */ "@/pages/InvitePage"));
const GuestJourneyDemoPage = lazy(() => import(/* webpackChunkName: "plan" */ "@/pages/GuestJourneyDemoPage"));
const CoupleAuthPage       = lazy(() => import(/* webpackChunkName: "plan" */ "@/pages/CoupleAuthPage"));
const JoinPage             = lazy(() => import(/* webpackChunkName: "plan" */ "@/pages/JoinPage"));
const StartPage            = lazy(() => import(/* webpackChunkName: "plan" */ "@/pages/StartPage"));

/* ── Page transition wrapper ── */
function PageTransition({ children }) {
  return (
    <div className="animate-page-in">
      {children}
    </div>
  );
}

/* ── Suspense fallback while a lazy route chunk loads ── */
function RouteFallback() {
  return (
    <div className="min-h-screen bg-ivory flex items-center justify-center">
      <div className="font-body text-xs tracking-[0.25em] uppercase text-charcoal/40 animate-pulse">Loading</div>
    </div>
  );
}

/* ── Guest-facing pages get bottom nav padding ── */
function GuestPage({ children }) {
  return <div className="pb-16">{children}</div>;
}

/* ── Renders GuestNav outside PageTransition so position:fixed works ── */
function GuestNavWrapper() {
  const location = useLocation();
  const isGuestPage = ['/checkin', '/photos', '/quiz'].some(p => location.pathname.includes(p));
  if (!isGuestPage) return null;
  // Extract weddingId from URL since we're outside <Routes>
  const match = location.pathname.match(/\/wedding\/([^/]+)\//);
  const weddingId = match ? match[1] : null;
  if (!weddingId) return null;
  return <GuestNav weddingId={weddingId} />;
}

/* ── Redirect that substitutes :params in the target path and preserves search/hash ── */
function RedirectWithParams({ to }) {
  const params = useParams();
  const { search, hash } = useLocation();
  const resolved = Object.entries(params).reduce(
    (acc, [key, value]) => acc.replaceAll(`:${key}`, value ?? ""),
    to
  );
  return <Navigate to={resolved + search + hash} replace />;
}

/* ── 404 page ── */
function NotFoundPage() {
  const navigate = useNavigate();
  return (
    <div className="min-h-screen bg-ivory flex items-center justify-center p-6">
      <div className="max-w-md text-center space-y-6">
        <p className="font-heading text-6xl text-gold font-bold">404</p>
        <div className="space-y-2">
          <h1 className="font-heading text-2xl text-navy font-bold">Page not found</h1>
          <p className="font-body text-charcoal/60">The page you're looking for doesn't exist or the link may be incorrect.</p>
        </div>
        <button
          onClick={() => navigate('/')}
          className="rounded-full bg-gradient-gold text-white shadow-lg hover:shadow-xl transition-all duration-300 font-medium tracking-wide uppercase px-8 py-3 text-sm"
        >
          Go Home
        </button>
      </div>
    </div>
  );
}

function AppRoutes() {
  const location = useLocation();
  const [renderLocation, setRenderLocation] = useState(location);

  // On route change: capture old DOM → update → animate via View Transitions API
  useEffect(() => {
    if (location.key === renderLocation.key) return;
    const update = () => {
      setRenderLocation(location);
      window.scrollTo(0, 0);
    };
    if (document.startViewTransition) {
      document.startViewTransition(() => flushSync(update));
    } else {
      update();
    }
  }, [location.key]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <>
      <PageTransition key={renderLocation.pathname}>
        <Suspense fallback={<RouteFallback />}>
        <Routes location={renderLocation}>
          <Route path="/" element={<LandingPage />} />

          {/* Plan Mode — Couple, pre-wedding */}
          <Route path="/plan/wedding/:weddingId/admin" element={<AdminPage />} />
          <Route path="/plan/learn/seating" element={<SeatingLesson />} />

          {/* Live Mode — launcher (install target) */}
          <Route path="/live" element={<LiveLauncher />} />
          <Route path="/live/wedding/:weddingId" element={<LiveLauncher />} />

          {/* Live Mode — day-of, operator facet */}
          <Route path="/live/wedding/:weddingId/checkin" element={<GuestPage><CheckinPage /></GuestPage>} />
          <Route path="/live/wedding/:weddingId/reception" element={<ReceptionPage />} />
          <Route path="/live/wedding/:weddingId/luckydraw" element={<LuckyDrawPage />} />
          <Route path="/live/wedding/:weddingId/tv" element={<TVPage />} />
          <Route path="/live/wedding/:weddingId/lobby" element={<LobbyPage />} />
          <Route path="/live/wedding/:weddingId/gallery" element={<GalleryPage />} />

          {/* Live Mode — day-of, guest facet */}
          <Route path="/live/wedding/:weddingId/quiz" element={<GuestPage><QuizPage /></GuestPage>} />
          <Route path="/live/wedding/:weddingId/photos" element={<GuestPage><PhotosPage /></GuestPage>} />
          <Route path="/live/wedding/:weddingId/invite" element={<InvitePage />} />
          <Route path="/rsvp/:token" element={<InvitePage />} />
          {/* Code-entry landing — guests type their group booking code (?code= auto-resolves) */}
          <Route path="/rsvp" element={<InvitePage />} />

          {/* Plan Mode — guided guest journey demo (couple sees what guests see) */}
          <Route path="/plan/wedding/:weddingId/guest-journey" element={<GuestJourneyDemoPage />} />

          {/* Couple auth — magic-link email sign-in */}
          {/* Post-sign-in landing: resolves the couple's wedding, or makes
              their first one. Both landing-page entry points route through
              here so no wedding id is minted before we know who they are. */}
          <Route path="/start" element={<StartPage />} />

          <Route path="/couple-auth" element={<CoupleAuthPage />} />
          <Route path="/couple-auth/complete" element={<CoupleAuthPage />} />

          {/* Member invite acceptance */}
          <Route path="/join/:weddingId/:memberId" element={<JoinPage />} />

          {/* Legacy URL redirects — preserve old QR codes, SMS links, bookmarks */}
          <Route path="/wedding/:weddingId/admin" element={<RedirectWithParams to="/plan/wedding/:weddingId/admin" />} />
          <Route path="/wedding/:weddingId/checkin" element={<RedirectWithParams to="/live/wedding/:weddingId/checkin" />} />
          <Route path="/wedding/:weddingId/reception" element={<RedirectWithParams to="/live/wedding/:weddingId/reception" />} />
          <Route path="/wedding/:weddingId/quiz" element={<RedirectWithParams to="/live/wedding/:weddingId/quiz" />} />
          <Route path="/wedding/:weddingId/luckydraw" element={<RedirectWithParams to="/live/wedding/:weddingId/luckydraw" />} />
          <Route path="/wedding/:weddingId/tv" element={<RedirectWithParams to="/live/wedding/:weddingId/tv" />} />
          <Route path="/wedding/:weddingId/photos" element={<RedirectWithParams to="/live/wedding/:weddingId/photos" />} />
          <Route path="/wedding/:weddingId/gallery" element={<RedirectWithParams to="/live/wedding/:weddingId/gallery" />} />
          <Route path="/wedding/:weddingId/invite" element={<RedirectWithParams to="/live/wedding/:weddingId/invite" />} />
          <Route path="/wedding/:weddingId/guest-journey" element={<RedirectWithParams to="/plan/wedding/:weddingId/guest-journey" />} />
          <Route path="/learn/seating" element={<Navigate to="/plan/learn/seating" replace />} />

          <Route path="*" element={<NotFoundPage />} />
        </Routes>
        </Suspense>
      </PageTransition>
      <GuestNavWrapper />
    </>
  );
}

function App() {
  return (
    <div className="App">
      <BrowserRouter>
        <ErrorBoundary>
          <AppRoutes />
        </ErrorBoundary>
      </BrowserRouter>
      <Toaster position="top-center" richColors />
    </div>
  );
}

export default App;
